import { useCallback, useLayoutEffect, useRef, useState } from 'react'

type NavOverflowOptions = {
  itemCount: number
  /** gaps between items in the nav */
  gap: number
  /**
   * Pass the pathname. Menu triggers label the active page, so their width changes with the route
   */
  remeasureKey?: string
}

export function useNavOverflow({ itemCount, gap, remeasureKey }: NavOverflowOptions) {
  const navRef = useRef<HTMLElement>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  const itemWidths = useRef<number[]>([])
  const [visibleCount, setVisibleCount] = useState(itemCount)

  const registerItem = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      if (el) itemWidths.current[index] = el.offsetWidth
    },
    [],
  )

  const measure = useCallback(() => {
    const nav = navRef.current
    const more = moreRef.current
    if (!nav || !more) return

    const available = nav.clientWidth - more.offsetWidth
    let used = 0
    let fit = 0
    for (const width of itemWidths.current) {
      used += fit === 0 ? width : width + gap
      if (used > available) break
      fit++
    }
    setVisibleCount(fit)
  }, [gap])

  useLayoutEffect(() => {
    const nav = navRef.current
    if (!nav) return

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(nav)

    return () => observer.disconnect()
  }, [measure])

  useLayoutEffect(measure, [measure, remeasureKey])

  return { navRef, moreRef, registerItem, visibleCount }
}
